// assets/AllCourses/Exercises/MatchingExercise.jsx
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function MatchingExercise({
  exercise = {},
  onUpdate,           // Edit mode only
  errors = {},
  isPreview = false,  // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student: report correct/incorrect
}) {
  const isEditMode = typeof onUpdate === "function";

  // Safe pairs
  const pairs = Array.isArray(exercise.matchingPairs)
    ? exercise.matchingPairs.map(p => ({
        question: typeof p.question === "string" ? p.question.trim() : "",
        answer: typeof p.answer === "string" ? p.answer.trim() : "",
        requireCapitalization: Boolean(p.requireCapitalization),
      }))
    : [];

  // Student state
  const [state, setState] = useState({
    matches: {}, // question index → shuffled answer index
    hasInteracted: false,
  });
  const [selectedQuestionIdx, setSelectedQuestionIdx] = useState(null);
  const completedRef = useRef(false);

  // Shuffle answers once per exercise (student mode)
  const shuffledAnswers = useMemo(() => {
    return pairs
      .map((p, idx) => ({ answer: p.answer, originalIndex: idx }))
      .sort(() => Math.random() - 0.5);
  }, [exercise._id, isPreview]);

  // Sync saved attempt (student mode)
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;
    setState({
      matches: typeof exercise.attempt.matches === "object" ? exercise.attempt.matches : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
    setSelectedQuestionIdx(null);
  }, [isPreview, exercise.attempt]);

  // Reset on exercise change
  useEffect(() => {
    if (isPreview) {
      completedRef.current = false;
      setSelectedQuestionIdx(null);
    }
  }, [exercise._id, isPreview]);

  // Normalize text for comparison
  const normalize = (text = "", requireCapitalization = false) => {
    let t = String(text).trim();
    if (!requireCapitalization) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  // === EDIT MODE FUNCTIONS ===
  const updatePairs = (next) => {
    if (isEditMode) onUpdate("matchingPairs", next);
  };

  const handlePairChange = (idx, field, value) => {
    if (!isEditMode) return;
    const next = [...pairs];
    next[idx] = { ...next[idx], [field]: value.trim() };
    updatePairs(next);
  };

  const handleCapitalizationChange = (idx, checked) => {
    if (!isEditMode) return;
    const next = [...pairs];
    next[idx] = { ...next[idx], requireCapitalization: Boolean(checked) };
    updatePairs(next);
  };

  const handleAddPair = () => {
    if (!isEditMode) return;
    updatePairs([
      ...pairs,
      { question: "", answer: "", requireCapitalization: false },
    ]);
  };

  const handleRemovePair = (idx) => {
    if (!isEditMode) return;
    updatePairs(pairs.filter((_, i) => i !== idx));
  };

  // === STUDENT MODE FUNCTIONS ===
  const handleQuestionClick = (qIdx) => {
    if (isEditMode || showResult || state.matches[qIdx] !== undefined) return;
    setSelectedQuestionIdx(qIdx);
    if (!state.hasInteracted) {
      setState(prev => ({ ...prev, hasInteracted: true }));
    }
  };

  const handleAnswerClick = (shuffledIdx) => {
    if (!isPreview || selectedQuestionIdx === null || showResult) return;

    setState(prev => {
      const next = {
        ...prev,
        matches: {
          ...prev.matches,
          [selectedQuestionIdx]: shuffledIdx,
        },
        hasInteracted: true,
      };

      setAttempt(next);
      setSelectedQuestionIdx(null);

      const allMatched = pairs.every((_, i) => next.matches[i] !== undefined);
      if (allMatched) {
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = (currentState) => {
    if (completedRef.current || !isPreview) return;
    completedRef.current = true;

    const allCorrect = pairs.every((pair, qIdx) => {
      const shuffledIdx = currentState.matches[qIdx];
      if (shuffledIdx === undefined) return false;
      const userAnswer = shuffledAnswers[shuffledIdx]?.answer || "";
      return normalize(userAnswer, pair.requireCapitalization) ===
             normalize(pair.answer, pair.requireCapitalization);
    });

    onComplete(exercise._id, allCorrect);
  };

  // === EDIT MODE RENDER ===
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {pairs.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No pairs yet. Click "Add Pair" to begin.
          </p>
        )}

        {pairs.map((p, idx) => (
          <Card key={idx} className="p-6 space-y-4">
            <StringField
              label={`Question ${idx + 1}`}
              value={p.question}
              onChange={(v) => handlePairChange(idx, "question", v)}
              placeholder="What is the capital of France?"
              required
              error={errors?.[`exercise-${exercise.index}-pair-${idx}-q`]}
            />

            <StringField
              label={`Answer ${idx + 1}`}
              value={p.answer}
              onChange={(v) => handlePairChange(idx, "answer", v)}
              placeholder="Paris"
              required
              error={errors?.[`exercise-${exercise.index}-pair-${idx}-a`]}
            />

            <CapitalizationRule
              checked={!!p.requireCapitalization}
              onChange={(c) => handleCapitalizationChange(idx, c)}
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemovePair(idx)}
            >
              Remove Pair
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddPair}>
          Add Pair
        </Button>
      </div>
    );
  }

  // === STUDENT PREVIEW MODE ===
  if (pairs.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No matching pairs provided.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardDescription>
          Click a question, then click its correct answer to match them.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-8">
        {/* Questions */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg mb-4">Questions</h3>
          {pairs.map((p, qIdx) => {
            const matchedAnswerIdx = state.matches[qIdx];
            const userAnswer = matchedAnswerIdx !== undefined
              ? shuffledAnswers[matchedAnswerIdx]?.answer
              : null;
            const isCorrect = userAnswer
              ? normalize(userAnswer, p.requireCapitalization) ===
                normalize(p.answer, p.requireCapitalization)
              : false;

            return (
              <Button
                key={qIdx}
                variant={
                  selectedQuestionIdx === qIdx
                    ? "secondary"
                    : userAnswer ? "outline" : "outline"
                }
                className={cn(
                  "w-full justify-start text-left h-auto py-4",
                  userAnswer && "opacity-60 cursor-not-allowed",
                  showResult && userAnswer && isCorrect && "bg-green-100 border-green-600",
                  showResult && userAnswer && !isCorrect && "bg-red-100 border-red-600"
                )}
                onClick={() => handleQuestionClick(qIdx)}
                disabled={!!userAnswer || showResult}
              >
                <span className="font-medium">{qIdx + 1}.</span>
                <span className="ml-3">{p.question}</span>
                {showResult && userAnswer && (
                  <span className="ml-auto text-sm">
                    {isCorrect ? "Correct" : `Wrong → ${p.answer}`}
                  </span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Answers */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg mb-4">Answers</h3>
          {shuffledAnswers.map((item, aIdx) => {
            const isUsed = Object.values(state.matches).includes(aIdx);

            return (
              <Button
                key={aIdx}
                variant="outline"
                className={cn(
                  "w-full justify-start text-left h-auto py-4",
                  isUsed && "opacity-60 cursor-not-allowed"
                )}
                onClick={() => handleAnswerClick(aIdx)}
                disabled={isUsed || showResult}
              >
                {item.answer}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}